import EmptyState from "@/components/EmptyState";
import Table from "@/components/Table";
import { Person, EducationCertification } from "@/types";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { GET_EDUCATION_CERTIFICATION_HISTORY_QUERY } from "./queries";
import { toFormat } from "@/lib/dateHelpers";
import MetricGrid from "@/components/MetricGrid";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import TabLayout from "./_TabLayout";
import { usePersonEducationAccess } from "../person/_usePersonEducationAccess";

type PersonData = { person: Person };

const formatTableData = (list: EducationCertification[]) =>
  list.map((e: EducationCertification, id: number) => ({
    id: id.toString(),
    programDescription: `${e.programDescription} (${e.programName})`,
    curriculumVersion: e.curriculumVersion,
    careerPath: e.careerPath,
    startDate: toFormat(e.startDate),
    endDate: e.endDate && toFormat(e.endDate),
    expirationDate: e.subscription?.endDate && toFormat(e.subscription.endDate)
  }));

export default function LearningDetails() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonEducation } = usePersonEducationAccess();

  const { data, loading, error } = useQuery<PersonData>(GET_EDUCATION_CERTIFICATION_HISTORY_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonEducation || !pgaId
  });

  if (!loaded) {
    return null;
  }

  if (error) return <div>Error: {error.message}</div>;

  const personData = data?.person;
  const certificationHistory = personData?.educationCertificationHistory;
  const latestCertification = personData?.educationLatestCertification;
  const tableData = !!certificationHistory?.length ? formatTableData(certificationHistory) : [];

  const metrics = latestCertification
    ? [
        {
          label: "Certification Program Type",
          value: latestCertification.programName
        },
        {
          label: "Career Path",
          value: latestCertification.careerPath
        },
        {
          label: "Start Date",
          value: toFormat(latestCertification.startDate)
        },
        {
          label: "Completion Date",
          value: latestCertification.endDate && toFormat(latestCertification.endDate)
        },
        {
          label: "Expiration Date",
          value: latestCertification.subscription?.endDate && toFormat(latestCertification.subscription.endDate)
        }
      ]
    : [];

  return (
    <TabLayout>
      {!!metrics.length ? (
        <>
          <MetricGrid metrics={metrics} layout="expanded" />
          <div className="pt-6" />
        </>
      ) : (
        <div className="pt-12" />
      )}
      <Table
        loading={loading}
        data={tableData}
        columns={[
          {
            header: "Program",
            id: "programDescription",
            accessorKey: "programDescription",
            size: 250,
            enableSorting: false,
            meta: {
              className: getStickyColumnClasses(!!tableData?.length)
            }
          },
          {
            header: "Version",
            id: "curriculumVersion",
            accessorKey: "curriculumVersion",
            enableSorting: false
          },
          {
            header: "Career Path",
            id: "careerPath",
            accessorKey: "careerPath",
            enableSorting: false
          },
          {
            header: "Start Date",
            id: "startDate",
            accessorKey: "startDate",
            enableSorting: false
          },
          {
            header: "End Date",
            id: "endDate",
            accessorKey: "endDate",
            enableSorting: false
          },
          {
            header: "Expiration",
            id: "expirationDate",
            accessorKey: "expirationDate",
            enableSorting: false
          }
        ]}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Programs"
            caption="This person has not started any lifelong learning programs"
            iconName="workspace_premium"
          />
        )}
        data-testid="learning-history-table"
      />
    </TabLayout>
  );
}
